import React, { createContext, useState, useEffect, useRef } from 'react'

const { storefrontClient } = require('../utils/shopify-client')

const StoreContext = createContext()
const isBrowser = typeof window !== 'undefined'
const localStorageKey = 'shopify_checkout_id'

const StoreProvider = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const [store, updateStore] = useState({
    client: storefrontClient,
    adding: false,
    didJustAddToCart: false,
    cart: {
      lines: { edges: [] },
      cost: {
        subtotalAmount: { amount: '0.00', currencyCode: 'CAD' },
        totalTaxAmount: { amount: '0.00', currencyCode: 'CAD' },
        totalAmount: { amount: '0.00', currencyCode: 'CAD' },
      },
      checkoutUrl: '/',
    },
    products: [],
    shop: {},
  })

  useEffect(() => {
    if (store.didJustAddToCart) {
      const timer = setTimeout(() => {
        updateStore(prev => ({ ...prev, didJustAddToCart: false }))
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [store.didJustAddToCart])

  const isRemoved = useRef(false)

  useEffect(() => {
    if (!isBrowser) return

    const initializeCheckout = async () => {
      setLoading(true)

      const storedId = localStorage.getItem(localStorageKey)

      const fetchCheckout = async cartId => {
        const res = await store.client.request(
          `query GetCart($id: ID!) {
            cart(id: $id) {
              id
              checkoutUrl
              createdAt
              updatedAt
              lines(first: 10) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        image { url altText }
                        price { amount currencyCode }
                        product { handle productType title }
                      }
                    }
                  }
                }
              }
              cost {
                subtotalAmount { amount currencyCode }
                totalAmount { amount currencyCode }
                totalTaxAmount { amount currencyCode }
              }
            }
          }`,
          { variables: { id: cartId } }
        )

        const cart = res?.data?.cart
        if (!cart) {
          console.error('[fetchCheckout] Cart not found for id:', cartId)
          throw new Error('Cart not found')
        }
        return cart
      }

      const setCheckoutInState = async cart => {
        const fullId = cart.id
        localStorage.setItem(localStorageKey, fullId)

        const safeCart = {
          ...cart,
          cost: {
            subtotalAmount: { amount: '0.00', currencyCode: 'CAD' },
            totalTaxAmount: { amount: '0.00', currencyCode: 'CAD' },
            totalAmount: { amount: '0.00', currencyCode: 'CAD' },
            ...cart.cost,
          },
        }

        updateStore(prev => ({ ...prev, cart: safeCart }))

        if (
          safeCart.lines?.edges?.length > 0 &&
          safeCart.cost?.subtotalAmount?.amount === '0.00'
        ) {
          try {
            const freshCart = await fetchCheckout(fullId)
            updateStore(prev => ({ ...prev, cart: freshCart }))
          } catch (e) {
            console.warn('[setCheckoutInState] Refetch failed:', e)
          }
        }
      }

      const createNewCheckout = async () => {
        const res = await store.client.request(`
          mutation cartCreate {
            cartCreate {
              cart {
                id
                checkoutUrl
                lines(first: 10) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          image { url altText }
                          price { amount currencyCode }
                          product { handle productType title }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount { amount currencyCode }
                  totalTaxAmount { amount currencyCode }
                  totalAmount { amount currencyCode }
                }
              }
            }
          }
        `)

        const cart = res?.data?.cartCreate?.cart
        if (!cart) throw new Error('Failed to create cart')

        // Optional: set buyer identity
        await store.client.request(`
          mutation {
            cartBuyerIdentityUpdate(
              cartId: "${cart.id}",
              buyerIdentity: { countryCode: US }
            ) {
              cart { id }
            }
          }
        `)

        return cart
      }

      let restored = false
      if (storedId) {
        try {
          const cart = await fetchCheckout(storedId)
          if (cart) {
            await setCheckoutInState(cart)
            setLoading(false)
            restored = true
          } else {
            console.warn(
              '[Cart Restore] Cart is completed or not found, removing localStorage'
            )
            localStorage.removeItem(localStorageKey)
          }
        } catch (err) {
          console.warn('[Cart Restore] Failed, clearing localStorage:', err)
          localStorage.removeItem(localStorageKey)
        }
      }

      if (!restored) {
        const freshCart = await createNewCheckout()
        await setCheckoutInState(freshCart)
        setLoading(false)
      }
    }

    initializeCheckout()
  }, [store.client])

  useEffect(
    () => () => {
      isRemoved.current = true
    },
    []
  )

  const addVariantToCart = async (variantId, quantity) => {
    if (!variantId || !quantity) {
      console.error('[StoreContext] Missing variantId or quantity:', {
        variantId,
        quantity,
      })
      return
    }

    updateStore(prev => ({ ...prev, adding: true }))
    const { cart, client } = store

    if (!cart?.id) {
      console.error('[StoreContext] Missing cart.id')
      updateStore(prev => ({ ...prev, adding: false }))
      return
    }

    try {
      const res = await client.request(
        `
          mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                id
                checkoutUrl
                lines(first: 10) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          image { url altText }
                          price { amount currencyCode }
                          product { handle productType title }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount { amount currencyCode }
                  totalTaxAmount { amount currencyCode }
                  totalAmount { amount currencyCode }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId: cart.id,
            lines: [
              {
                quantity: parseInt(quantity, 10),
                merchandiseId: variantId,
              },
            ],
          },
        }
      )

      const updatedCart = res?.data?.cartLinesAdd?.cart
      const userErrors = res?.data?.cartLinesAdd?.userErrors

      if (userErrors?.length) {
        console.error('[StoreContext] Shopify cart userErrors:', userErrors)
      }

      if (updatedCart) {
        updateStore(prev => ({
          ...prev,
          cart: JSON.parse(JSON.stringify(updatedCart)),
          adding: false,
          didJustAddToCart: true,
        }))
      } else {
        console.error('Add to cart failed:', res.data?.cartLinesAdd?.userErrors)
        updateStore(prev => ({ ...prev, adding: false }))
      }
    } catch (error) {
      console.error('cartLinesAdd error:', error)
      updateStore(prev => ({ ...prev, adding: false }))
    }
  }

  const cartLinesRemove = async (cartId, lineItemId) => {
    try {
      const res = await store.client.request(
        `
          mutation RemoveLine($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
              cart {
                id
                checkoutUrl
                lines(first: 10) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          image { url altText }
                          price { amount currencyCode }
                          product { handle productType title }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount { amount currencyCode }
                  totalTaxAmount { amount currencyCode }
                  totalAmount { amount currencyCode }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId,
            lineIds: [lineItemId],
          },
        }
      )

      const updatedCart = res?.data?.cartLinesRemove?.cart
      const userErrors = res?.data?.cartLinesRemove?.userErrors

      if (userErrors?.length) {
        console.error('[cartLinesRemove] Shopify errors:', userErrors)
      }

      if (updatedCart) {
        updateStore(prev => ({
          ...prev,
          cart: JSON.parse(JSON.stringify(updatedCart)),
        }))
      }
    } catch (err) {
      console.error('[cartLinesRemove] Error:', err)
    }
  }

  const updateLineItem = async (cartId, lineItemId, quantity) => {
    try {
      const res = await store.client.request(
        `
          mutation UpdateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                id
                checkoutUrl
                lines(first: 10) {
                  edges {
                    node {
                      id
                      quantity
                      merchandise {
                        ... on ProductVariant {
                          id
                          title
                          price {
                            amount
                            currencyCode
                          }
                          image {
                            url
                            altText
                          }
                          product {
                            handle
                            productType
                            title
                          }
                        }
                      }
                    }
                  }
                }
                cost {
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalTaxAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        {
          variables: {
            cartId,
            lines: [
              {
                id: lineItemId,
                quantity: parseInt(quantity, 10),
              },
            ],
          },
        }
      )

      const updatedCart = res?.data?.cartLinesUpdate?.cart
      const userErrors = res?.data?.cartLinesUpdate?.userErrors

      if (userErrors?.length) {
        console.error('[StoreContext] cartLinesUpdate userErrors:', userErrors)
      }

      if (updatedCart) {
        updateStore(prev => ({
          ...prev,
          cart: JSON.parse(JSON.stringify(updatedCart)),
        }))
      }
    } catch (err) {
      console.error('[StoreContext] cartLinesUpdate error:', err)
    }
  }

  return (
    <StoreContext.Provider
      value={{
        client: store.client,
        cart: store.cart,
        adding: store.adding,
        didJustAddToCart: store.didJustAddToCart,
        addVariantToCart,
        cartLinesRemove,
        updateLineItem,
        loading,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}

export { StoreContext, StoreProvider }
